import { useEffect, useRef, useState } from 'react';
import { Modal } from '../../components/ui';

/**
 * The Culture Builder, hosted so its result can land in the V/TO.
 *
 * A self-contained four-step page — pick an archetype, pick the value
 * categories that matter, pick the values inside them, and it groups the
 * selection into a handful of named principles with a sentence each. It is
 * somebody else's standalone page, brought across from MSP Toolbox, and it
 * brings its own CSS reset, its own palette and a top-level script, none of
 * which should land on the rest of the app. So it lives in an isolated iframe.
 *
 * SERVED AS STATIC FILES, NOT srcDoc — the KPI Wheel's lesson. MSP Toolbox
 * frames it by srcDoc, but a srcDoc frame inherits this page's CSP, and the
 * production policy is `script-src 'self'` with helmet's default
 * `script-src-attr 'none'`: the builder's inline <script> and every inline
 * onclick would be blocked on the deployed app while passing every local
 * check (the CSP only applies in production). So in `web/public/culture-builder`
 * the script is its own file, builder.js, its inline handlers are data-action
 * attributes behind one delegated listener, and the rest of its logic is the
 * original line for line. palette.css re-skins it and bridge.js adds the
 * button that posts the result up.
 *
 * WHAT COMES BACK, AND WHY IT IS THE PRINCIPLES RATHER THAN THE VALUES. The
 * builder ends with two lists: five-ish principles, and the twenty-odd value
 * words that produced them. EOS core values are the short list you hire, fire
 * and review against — "the handful of behaviours", as the V/TO page puts it —
 * so the principles are the ones that fit, each already carrying a sentence
 * describing what it looks like in practice. The value words come across as
 * evidence appended to that sentence, which is what makes a principle
 * arguable rather than a slogan.
 */

/** One principle, as the builder's result carries it. */
export interface CulturePrinciple {
  name: string;
  description: string;
  values: string[];
}

interface Props {
  open: boolean;
  /** How many core values the V/TO already holds — changes what the note says,
   *  because adding to a list is a different act from filling an empty one. */
  existingCount: number;
  onClose: () => void;
  /** Resolves when the values have been written to the V/TO. */
  onAdopt: (principles: CulturePrinciple[]) => Promise<void>;
}

export function CultureBuilder({ open, existingCount, onClose, onAdopt }: Props) {
  const frame = useRef<HTMLIFrameElement | null>(null);
  const adopting = useRef(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    function onMessage(event: MessageEvent) {
      /* The frame itself, not merely the same origin: the builder is served
         from this origin, so an origin check alone would also accept a message
         posted by any other same-origin frame on the page. */
      if (event.source !== frame.current?.contentWindow) return;
      const data = event.data as { type?: string; principles?: CulturePrinciple[] } | null;
      if (!data || data.type !== 'vto-core-values') return;

      const principles = (data.principles ?? []).filter(
        (p) => p && typeof p.name === 'string' && p.name.trim() !== '',
      );
      if (principles.length === 0) {
        setProblem('That run produced no principles — pick a few values and generate again.');
        return;
      }
      /* A ref, not the busy state: a second message can arrive before the
         re-render that would carry busy=true, and a second run of onAdopt
         appends every principle again. The bridge disabling its button is
         not something this side should rely on. */
      if (adopting.current) return;
      adopting.current = true;
      setBusy(true);
      setProblem(null);
      void onAdopt(principles)
        .then(() => onClose())
        .catch((err: unknown) =>
          setProblem(err instanceof Error ? err.message : 'Could not save those values'),
        )
        .finally(() => {
          adopting.current = false;
          setBusy(false);
        });
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [open, onAdopt, onClose]);

  if (!open) return null;

  return (
    <Modal title="Culture Builder" onClose={onClose}>
      <p className="small muted">
        Work through the four steps. At the end, <strong>Use these as the V/TO core values</strong>{' '}
        puts the principles it produces straight into the V/TO — you can edit or remove any of them
        afterwards.
        {existingCount > 0 && (
          <>
            {' '}
            They are <strong>added to</strong> the {existingCount} core value
            {existingCount === 1 ? '' : 's'} already there; nothing is replaced.
          </>
        )}
      </p>
      {problem && <p className="small" style={{ color: 'var(--status-danger)' }}>{problem}</p>}
      {busy && <p className="small">Adding them to the V/TO…</p>}
      <iframe
        ref={frame}
        title="Culture Builder"
        src="/culture-builder/index.html"
        style={{
          width: '100%',
          height: 'clamp(480px, calc(100vh - 260px), 900px)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius)',
          background: '#f9f8f6',
        }}
      />
    </Modal>
  );
}
